/**
 * Pure complete-check helpers (no Firebase). Keep this importable from unit
 * tests without booting Auth / Pinia.
 */

import {
  WORKSPACE_ROTATOR_ROLES,
  WORKSPACE_ROTATOR_EXCLUDED_ROLES,
  isWorkspaceRotatorEmail
} from '@/utils/workspaceRoleRotator'

export const COMPLETE_CHECK_CAVEAT =
  'Same demo login, same UID. Rotating the role tests the security rules for that JWT. Referral access creates disposable schedule/provider/share assignments and separate owned/unowned evaluations; other scenarios do not seed a teacher caseload or para aide schedule. District Admin is included. District roles runs all three case-manager roles with real allSchools claims on two disposable campuses once per sweep. Separate-account, aide-schedule and browser checks remain necessary.'

export function scenarioIdsForPreset(includeWrites, allScenarios = []) {
  return allScenarios.filter((s) => includeWrites || !s.destructive).map((s) => s.id)
}

export function completeCheckRoleValues() {
  return WORKSPACE_ROTATOR_ROLES.map((r) => r.value)
}

/**
 * Roles a complete check will rotate through.
 * Null or omitted means every rotator role. An explicit list keeps only
 * rotator roles, in rotator order. District CM scope is tested by districtAccess.
 */
export function resolveCompleteCheckRoles(selectedRoles) {
  const all = completeCheckRoleValues()
  if (selectedRoles == null) return all
  const picked = new Set(selectedRoles)
  return all.filter((role) => picked.has(role))
}

export function assertCompleteCheckAllowed({ email, jwtRole } = {}) {
  if (!isWorkspaceRotatorEmail(email)) {
    return {
      ok: false,
      reason:
        'Complete check is only available for the allowlisted demo login. Use admin@casemanagevue.com, or sign in as each test-role account.'
    }
  }
  if (!jwtRole) {
    return { ok: false, reason: 'Complete check needs a JWT role claim before it can rotate.' }
  }
  if (WORKSPACE_ROTATOR_EXCLUDED_ROLES.includes(jwtRole)) {
    return {
      ok: false,
      reason: 'Complete check cannot restore this excluded role.'
    }
  }
  if (!completeCheckRoleValues().includes(jwtRole)) {
    return {
      ok: false,
      reason: `Complete check cannot start as ${jwtRole} — that role is not on the rotator, so it cannot restore afterward.`
    }
  }
  return { ok: true }
}
